import { ArrowUp, Mail, MapPin, Linkedin, Twitter } from 'lucide-react';
import { COMPANY, NAV_LINKS, LOGO_URL } from '../data/mock';

const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 00-.79-.05A6.34 6.34 0 003.15 15.2a6.34 6.34 0 0010.86 4.42V13.2a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-.81-.07 4.83 4.83 0 01-.38-4.56z" />
  </svg>
);

const XIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

export const Footer = () => {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleNavClick = (href) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <footer className="relative bg-[#0D1B2A] pt-20 pb-8">
      {/* CTA Bar */}
      <div className="max-w-7xl mx-auto px-6 -mt-32 mb-20 relative z-10">
        <div className="bg-gradient-to-r from-[#0B3D5C] to-[#147DAB] rounded-3xl p-10 md:p-14 flex flex-col md:flex-row items-center justify-between gap-8 shadow-2xl shadow-cyan-900/30">
          <div>
            <h3 className="text-3xl md:text-4xl font-bold text-white">
              Ready to Power Your Project?
            </h3>
            <p className="text-white/60 mt-2 text-base" dir="rtl">هل أنتم مستعدون لتشغيل مشروعكم؟</p>
          </div>
          <button
            onClick={() => handleNavClick('#contact')}
            className="flex-shrink-0 px-8 py-4 bg-[#D4952B] hover:bg-[#C08525] text-white font-semibold rounded-xl transition-all duration-300 shadow-xl shadow-amber-900/30 hover:shadow-amber-800/50 hover:-translate-y-0.5 text-sm tracking-wide"
          >
            Start a Conversation
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6">
        <div className="grid md:grid-cols-4 gap-12 pb-12 border-b border-white/8">
          {/* Brand */}
          <div className="md:col-span-2">
            <div className="flex items-center gap-3 mb-5">
              <div className="bg-white rounded-xl px-3 py-1.5">
                <img
                  src={LOGO_URL}
                  alt={COMPANY.name}
                  className="h-10 w-auto object-contain"
                />
              </div>
            </div>
            <p className="text-white/40 text-sm leading-relaxed max-w-sm">
              {COMPANY.subSlogan}
            </p>
            {/* Social Links */}
            <div className="flex items-center gap-3 mt-6">
              <a
                href={COMPANY.social.twitter}
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-lg bg-white/5 hover:bg-[#D4952B]/20 flex items-center justify-center text-white/50 hover:text-[#E8B84A] transition-all duration-300"
                aria-label="X (Twitter)"
              >
                <XIcon className="w-4 h-4" />
              </a>
              <a
                href={COMPANY.social.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-lg bg-white/5 hover:bg-[#D4952B]/20 flex items-center justify-center text-white/50 hover:text-[#E8B84A] transition-all duration-300"
                aria-label="LinkedIn"
              >
                <Linkedin className="w-4 h-4" />
              </a>
              <a
                href={COMPANY.social.tiktok}
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-lg bg-white/5 hover:bg-[#D4952B]/20 flex items-center justify-center text-white/50 hover:text-[#E8B84A] transition-all duration-300"
                aria-label="TikTok"
              >
                <TikTokIcon className="w-4 h-4" />
              </a>
              <a
                href={`mailto:${COMPANY.email}`}
                className="w-9 h-9 rounded-lg bg-white/5 hover:bg-[#D4952B]/20 flex items-center justify-center text-white/50 hover:text-[#E8B84A] transition-all duration-300"
                aria-label="Email"
              >
                <Mail className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white/90 font-semibold text-sm mb-5">Quick Links</h4>
            <div className="space-y-3">
              {NAV_LINKS.map((link) => (
                <button
                  key={link.href}
                  onClick={() => handleNavClick(link.href)}
                  className="block text-white/40 hover:text-[#E8B84A] text-sm transition-colors duration-300"
                >
                  {link.label}
                </button>
              ))}
            </div>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-white/90 font-semibold text-sm mb-5">Contact</h4>
            <div className="space-y-3 text-sm text-white/40">
              <p>{COMPANY.phone}</p>
              <p>{COMPANY.email}</p>
              <p>{COMPANY.hours}</p>
              <p className="leading-relaxed">{COMPANY.location}</p>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-4 pt-8">
          <div className="text-white/25 text-xs">
            &copy; {new Date().getFullYear()} {COMPANY.fullName}. All rights reserved.
          </div>
          <div className="text-white/25 text-xs font-['Noto_Sans_Arabic']" dir="rtl">
            {COMPANY.fullNameAr}
          </div>
          <button
            onClick={scrollToTop}
            className="w-10 h-10 rounded-full bg-white/5 hover:bg-[#D4952B]/20 flex items-center justify-center text-white/40 hover:text-[#E8B84A] transition-all duration-300"
          >
            <ArrowUp className="w-4 h-4" />
          </button>
        </div>
      </div>
    </footer>
  );
};
